import { ArchBaseService } from "@/common/api.service";
import authService from "@/services/auth/authService";
import {
  USER_LOGIN,
  ENTRA_ID_LOGIN,
  HANDLE_REDIRECT,
  UNIFIED_LOGOUT,
  REFRESH_TOKEN,
  VALIDATE_AUTH,
  COUNTRYLIST,
  SET_COUNTRY,
  SET_SOURCE_COUNTRY,
  SOURCE_COUNTRY,
} from "./actions.type";
import {
  ISLOADING,
  SET_CURRENT_USER,
  SET_AUTH_METHOD,
  SET_MSAL_ACCOUNT,
  SET_USER_PROFILE,
  SET_IS_AUTHENTICATED,
  CLEAR_AUTH,
} from "./mutations.type";

const state = {
  isLoading: false,
  currentuser: JSON.parse(localStorage.getItem("currentUser")) || [],
  countryList: [],
  sourcecountryList: [],
  // New auth state
  authMethod: localStorage.getItem("authMethod") || null,
  msalAccount: null,
  userProfile: null,
  isAuthenticated: false,
};

const getters = {
  currentuser(state) {
    return state.currentuser;
  },
  isLoading(state) {
    return state.isLoading;
  },
  countryList(state) {
    return state.countryList;
  },
  sourcecountryList(state) {
    return state.sourcecountryList;
  },
  // New getters for dual auth
  authMethod(state) {
    return state.authMethod;
  },
  isAuthenticated(state) {
    return state.isAuthenticated || authService.isAuthenticated();
  },
  userProfile(state) {
    return state.userProfile;
  },
  msalAccount(state) {
    return state.msalAccount;
  },
  unifiedUser(state) {
    // Returns user info regardless of auth method
    if (state.authMethod === "entraId") {
      return state.userProfile || state.msalAccount;
    }
    return state.currentuser;
  },
};

const actions = {
  // Traditional login
  async [USER_LOGIN](context, payload) {
    context.commit(ISLOADING, true);
    try {
      const result = await authService.loginWithCredentials(payload);
      context.commit(ISLOADING, false);

      if (result.success) {
        context.commit(SET_CURRENT_USER, result.user);
        context.commit(SET_AUTH_METHOD, "traditional");
        context.commit(SET_IS_AUTHENTICATED, true);

        return {
          status: 200,
          data: result.user,
        };
      } else {
        return {
          status: 401,
          data: { message: result.error },
        };
      }
    } catch (error) {
      context.commit(ISLOADING, false);
      return {
        status: 500,
        data: { message: error.message },
      };
    }
  },

  // Entra ID login
  async [ENTRA_ID_LOGIN](context, { usePopup = true } = {}) {
    context.commit(ISLOADING, true);
    try {
      // Use retry-enabled login for popup, regular login for redirect
      const result = usePopup
        ? await authService.loginWithEntraIdWithRetry(usePopup)
        : await authService.loginWithEntraId(usePopup);

      if (result.success) {
        if (result.redirect) {
          // Redirect login initiated, will be handled on callback
          return { status: 200, redirect: true };
        }

        // Store both traditional user data (from backend) and Entra ID profile
        if (result.user) {
          context.commit(SET_CURRENT_USER, result.user);
        }
        context.commit(SET_AUTH_METHOD, "entraId");
        context.commit(SET_MSAL_ACCOUNT, result.account);
        context.commit(SET_USER_PROFILE, result.profile);
        context.commit(SET_IS_AUTHENTICATED, true);
        context.commit(ISLOADING, false);

        return {
          status: 200,
          data: {
            account: result.account,
            profile: result.profile,
            user: result.user,
            token: result.token,
          },
        };
      } else {
        context.commit(ISLOADING, false);

        // Check if we should suggest redirect fallback
        if (result.suggestRedirect) {
          return {
            status: 409,
            data: {
              message: result.message || result.error,
              error: result.error,
              suggestRedirect: true,
            },
          };
        }

        // Map specific error codes to HTTP status codes
        let status = 401;
        if (result.error === "USER_NOT_FOUND") {
          status = 404;
        } else if (result.error === "NETWORK_ERROR") {
          status = 503;
        } else if (result.error === "INVALID_TOKEN") {
          status = 401;
        }

        return {
          status,
          data: {
            message: result.message || result.error,
            error: result.error,
          },
        };
      }
    } catch (error) {
      context.commit(ISLOADING, false);
      return {
        status: 500,
        data: {
          message: error.message || "An unexpected error occurred",
          error: "UNEXPECTED_ERROR",
        },
      };
    }
  },

  // Handle redirect from Entra ID
  async [HANDLE_REDIRECT](context) {
    try {
      const result = await authService.handleRedirectPromise();
      if (result?.success) {
        // Store both traditional user data (from backend) and Entra ID profile
        if (result.user) {
          context.commit(SET_CURRENT_USER, result.user);
        }
        context.commit(SET_AUTH_METHOD, "entraId");
        context.commit(SET_MSAL_ACCOUNT, result.account);
        context.commit(SET_IS_AUTHENTICATED, true);

        // Store profile from result
        if (result.profile) {
          context.commit(SET_USER_PROFILE, result.profile);
        }

        return { success: true, user: result.user };
      }

      // Return error information if redirect handling failed
      return {
        success: false,
        error: result?.error,
        message: result?.message,
      };
    } catch (error) {
      console.error("Handle redirect failed:", error);
      return {
        success: false,
        error: "REDIRECT_ERROR",
        message: error.message || "An error occurred during redirect handling",
      };
    }
  },

  // Unified logout
  async [UNIFIED_LOGOUT](context) {
    context.commit(ISLOADING, true);
    try {
      await authService.logout();
      context.commit(CLEAR_AUTH);
      context.commit(ISLOADING, false);
      return { success: true };
    } catch (error) {
      console.error("Logout failed:", error);
      context.commit(CLEAR_AUTH);
      context.commit(ISLOADING, false);
      return { success: true }; // Still clear local state even if logout fails
    }
  },

  // Refresh token
  async [REFRESH_TOKEN](context) {
    try {
      const token = await authService.refreshToken();

      // Update stored token if refresh successful
      if (token) {
        localStorage.setItem("jwtToken", token);

        // Update token expiration if using Entra ID
        if (context.state.authMethod === "entraId") {
          const entraIdAuth = (await import("@/services/auth/entraIdAuth"))
            .default;
          if (entraIdAuth.tokenExpiresAt) {
            localStorage.setItem(
              "tokenExpiresAt",
              entraIdAuth.tokenExpiresAt.toString(),
            );
          }
        }
      }

      return { success: true, token };
    } catch (error) {
      console.error("Token refresh failed:", error);
      context.commit(CLEAR_AUTH);
      return { success: false, error: error.message };
    }
  },

  // Validate authentication
  async [VALIDATE_AUTH](context) {
    try {
      const isValid = await authService.validateToken();
      if (!isValid) {
        context.commit(CLEAR_AUTH);
      }
      return isValid;
    } catch (error) {
      console.error("Auth validation failed:", error);
      context.commit(CLEAR_AUTH);
      return false;
    }
  },
  async [COUNTRYLIST](context) {
    ArchBaseService.init();
    const response = await ArchBaseService.get(
      "country/list?sort=asc&sortby=countryname&size=1000&page=1",
    );
    const data = response.data?.details || [];

    if (data && Array.isArray(data)) {
      data.map((country) => {
        // Transform the new API response format
        country.currency = country.currencycode || ""; // Keep original case (GB, US, etc.)
        country.isoCode = country.isocode || ""; // Keep original case (GB, US, etc.)
        country.isocode = country.isocode || ""; // Keep original case (GB, US, etc.)
        country.name = country.countryname; // Keep original country name
        country.dialCode = country.mobilecountrycode || ""; // Already has + prefix
        country.flagCode = country.isocode ? country.isocode.toLowerCase() : ""; // Lowercase for flag icons

        // Add additional fields from new API
        country.alpha2 = country.alpha2 || "";
        country.alpha3 = country.alpha3 || "";
        country.isonumeric = country.isonumeric || "";
        country.domestic = country.domestic || 0;
        country.crossborder = country.crossborder || 0;
        country.destination = country.destination || 0;
        country.isblacklisted = country.isblacklisted || 0;
      });
      context.commit(SET_COUNTRY, data);
    } else {
      context.commit(SET_COUNTRY, []);
    }
    return data;
  },
  async [SOURCE_COUNTRY](context) {
    ArchBaseService.init();
    const response = await ArchBaseService.get(
      "country/list?sort=asc&sortby=countryname&size=1000&page=1",
    );
    const data = response.data?.details || [];

    if (data && Array.isArray(data)) {
      data.map((country) => {
        // Transform the new API response format
        country.currency = country.currencycode || ""; // Keep original case (GB, US, etc.)
        country.isoCode = country.isocode || ""; // Keep original case (GB, US, etc.)
        country.isocode = country.isocode || ""; // Keep original case (GB, US, etc.)
        country.name = country.countryname; // Keep original country name
        country.dialCode = country.mobilecountrycode || ""; // Already has + prefix
        country.flagCode = country.isocode ? country.isocode.toLowerCase() : ""; // Lowercase for flag icons

        // Add additional fields from new API
        country.alpha2 = country.alpha2 || "";
        country.alpha3 = country.alpha3 || "";
        country.isonumeric = country.isonumeric || "";
        country.domestic = country.domestic || 0;
        country.crossborder = country.crossborder || 0;
        country.destination = country.destination || 0;
        country.isblacklisted = country.isblacklisted || 0;
      });
      context.commit(SET_SOURCE_COUNTRY, data);
    } else {
      context.commit(SET_SOURCE_COUNTRY, []);
    }
    return data;
  },
};

const mutations = {
  [SET_CURRENT_USER](state, data) {
    state.currentuser = data;
    localStorage.setItem("currentUser", JSON.stringify(data));
    // Keep the flat "userid" key in sync for both auth methods (traditional
    // login and Entra ID). Components read localStorage.getItem("userid")
    // when populating createdBy/updatedBy fields.
    if (data && data.userid) {
      localStorage.setItem("userid", data.userid);
    }
  },
  [ISLOADING](state, loading) {
    state.isLoading = loading;
  },
  [SET_COUNTRY](state, data) {
    state.countryList = data;
  },
  [SET_SOURCE_COUNTRY](state, data) {
    state.sourcecountryList = data;
  },
  // New mutations for dual auth
  [SET_AUTH_METHOD](state, method) {
    state.authMethod = method;
  },
  [SET_MSAL_ACCOUNT](state, account) {
    state.msalAccount = account;
  },
  [SET_USER_PROFILE](state, profile) {
    state.userProfile = profile;
  },
  [SET_IS_AUTHENTICATED](state, isAuth) {
    state.isAuthenticated = isAuth;
  },
  [CLEAR_AUTH](state) {
    state.authMethod = null;
    state.msalAccount = null;
    state.userProfile = null;
    state.currentuser = [];
    state.isAuthenticated = false;
    // Clear the persisted current-user keys so a stale userid cannot linger
    // after logout/session expiry (the flat "userid" key is set by SET_CURRENT_USER).
    localStorage.removeItem("currentUser");
    localStorage.removeItem("userid");
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
